import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import ReserveForZeroTag from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroTag'
import CruisesDiscountTsAndCsModal from 'components/Cruises/CruisesDiscountTsAndCsModal'
import TextButton from 'LuxKit/Button/TextButton'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import PriceRowMarginText from 'components/OfferPage/PriceRowMarginText'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import ModalContext from 'contexts/ModalContext'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import { useAppSelector } from 'hooks/reduxHooks'
import { useDirectSearchPrices, useSearchPrices } from 'hooks/Search/useSearchPrices'
import { EmptyArray, unique } from 'lib/array/arrayUtils'
import { hasIncludedGuestsExceeded } from 'lib/checkout/cartReservationUtils'
import getLPCRegionLOS from 'lib/offer/getLPCRegionLOS'
import offerPageURL from 'lib/offer/offerPageURL'
import { isVillaOffer } from 'lib/offer/offerTypes'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import {
  buildSuggestedDatesString,
  formatDateRangesForAlternativeDates,
  getShouldUseSearchPrices,
} from 'lib/search/searchUtils'

import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import type moment from 'moment'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { getIsMultiRoomEnabled } from 'selectors/featuresSelectors'
import { FormattedMessage, useIntl } from 'react-intl'
import useSearchBurnPoints from 'hooks/useSearchBurnPoints'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import getHotelEncryptedMargin from 'loyalty/lib/margin/getHotelEncryptedMargin'
import LoyaltyBurnPriceDisplay from 'loyalty/components/LoyaltyBurnPriceDisplay'
import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import styled from 'styled-components'
import Caption from 'LuxKit/Typography/Caption'

interface Props {
  offer: App.Offer | App.OfferSummary
  className?: string
  pkg: App.Package
  availableRate?: App.OfferAvailableRate
  hasDates?: boolean
  align?: 'start' | 'center' | 'end'
  desktopAlign?: 'start' | 'center' | 'end'
  /**
   * @default The offer page URL with no parameters
   */
  offerUrl?: string
  checkInDate?: moment.Moment
  filters?: App.OfferListFilters
  showCompact?: boolean
  compactExperimentLayout?: boolean
  isPricePerNight?: boolean
  hideIncludesTaxesAndFees?: boolean
  duration?: number
  hidePrice?: boolean
  overrideRoomsNumber?: number
}

interface MappedStateProps {
  isSpoofed: boolean
  pathname: string
}
// Export is only temporary for experiment
export const URGENCY_TAG_THRESHOLD = 5

const CompactPricingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow: hidden;
  white-space: nowrap;
`

const CompactAgentHubCommission = styled.div`
  text-align: left;

  &:empty {
    display: none;
  }
`

function OfferTilePricing(props: Props & MappedStateProps) {
  const {
    offer,
    className,
    pkg,
    availableRate,
    hasDates,
    align,
    desktopAlign,
    offerUrl,
    checkInDate,
    isSpoofed,
    pathname,
    filters,
    showCompact = false,
    compactExperimentLayout = false,
    isPricePerNight,
    hideIncludesTaxesAndFees = false,
    duration,
    hidePrice = false,
    overrideRoomsNumber,
  } = props

  const intl = useIntl()
  const offerMetadata = useOfferMetaData(offer.id, filters)
  const isCruise = (offer.holidayTypes ?? []).map(i => i.toLowerCase()).includes('cruises')
  const isVillas = isVillaOffer(offer)
  const rooms = filters?.rooms ?? EmptyArray
  const showMarginInSpoofMode = useAppSelector(state => state.system.showMarginInSpoofMode)

  const directSearchPrices = useDirectSearchPrices({ filters: filters ?? {}, offerId: offer.id })
  const shouldUseRealSearchPrices = getShouldUseSearchPrices(filters)
  const {
    defaultPricing,
    memberPricing,
    hotelPrice,
    hotelMemberPrice,
    showMemberPrice,
    propertyFees,
    suggestedDates,
  } = useSearchPrices({
    offer,
    pkg,
    rate: availableRate,
    duration,
    filters,
    useRealSearchPrices: shouldUseRealSearchPrices,
    showPerNightPricing: isPricePerNight && offer.type === OFFER_TYPE_HOTEL,
  })

  const useLuxPlusPricing = useAppSelector(getCanViewLuxPlusBenefits)
  const isMultiRoomEnable = useAppSelector(state => getIsMultiRoomEnabled(state, offer))

  const discountPercent = (showMemberPrice && memberPricing && useLuxPlusPricing) ? memberPricing.discountPercent : defaultPricing.discountPercent

  const hasSuggestedCheckInCheckOut = suggestedDates?.checkIn && suggestedDates?.checkOut
  const valueToDisplay = defaultPricing.value
  const taxesAndFees = (availableRate?.taxesAndFees ?? pkg.taxesAndFees) || 0
  const showPriceDetails = taxesAndFees > 0 || propertyFees > 0 || config.agentHub.isEnabled

  const shouldShowValue = pkg.value > 0 && !!discountPercent && pkg.shouldDisplayValue &&
    (offer.type == OFFER_TYPE_HOTEL || !pkg.roomRate?.inclusionsHideValue)

  const showIncludedGuestsExceeded = useMemo(() => {
    if (directSearchPrices) {
      return false
    }

    if (!pkg.roomType || (pkg.roomRate?.extraGuestSurcharges?.length || 0) > 0) {
      return false
    }

    return rooms?.some(room => hasIncludedGuestsExceeded(room, pkg, offer))
  }, [directSearchPrices, pkg, rooms, offer])

  const shouldShowRate = !offer.isDiscountPillHidden && discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  let staysFrom = offer.minDuration
  const LPCRegionLOS = getLPCRegionLOS(offer)
  if (LPCRegionLOS && offer.packages.some(p => p.duration === LPCRegionLOS)) {
    staysFrom = LPCRegionLOS
  }
  const showStaysFromLabel = !showCompact && !hasDates && offer.type === OFFER_TYPE_ALWAYS_ON && LPCRegionLOS && (staysFrom ?? 0) < (duration ?? 0)

  const isReserveForZeroEnabled = config.RESERVE_FOR_ZERO_ENABLED
  const isReservableForZero = (isReserveForZeroEnabled || isSpoofed) && isPackageEligibleForZeroReservation(offer, pkg.roomRate?.isReservableForZero, checkInDate)
  const adjustNights = useCallback((count: number) => {
    return isCruise && count ? count - 1 : count
  }, [isCruise])

  const utmSource = useAppSelector(state => state.utm.source)
  const noGoogleAffiliations = utmSource !== 'google'

  const durationText = useMemo(() => {
    const isFlexibleSearch = filters?.flexibleNights && suggestedDates?.checkIn && suggestedDates?.checkOut
    const isAnytimeSearch = (!filters?.checkIn && !filters?.checkOut) && !isFlexibleSearch
    const roomCount = overrideRoomsNumber || filters?.rooms?.length || 0
    const showRooms = isMultiRoomEnable && roomCount > 0
    const isAlwaysOnRoomOnly = offer.type === OFFER_TYPE_ALWAYS_ON && noGoogleAffiliations && !showCompact
    type Suffix = 'from' | 'roomOnly'
    const suffix: Suffix = isAlwaysOnRoomOnly ? 'roomOnly' : 'from'

    const roomsText = showRooms ? intl.formatMessage(
      {
        id: 'wNIAQxc_tHFdfW1RYsY6',
        defaultMessage: '{count, plural, one {# room} other {# rooms}}',
        description: 'Room count for offer tile pricing on the hotel or room booking page',
      },
      { count: roomCount },
    ) : ''

    const formatNightsWithRooms = (nightCount: number) => {
      const nights = adjustNights(nightCount)
      if (showRooms) {
        switch (suffix) {
          case 'roomOnly':
            return intl.formatMessage(
              {
                id: 'z4zf7jl3HOIonVJn-Hxd',
                defaultMessage: '{nights, plural, one {# night} other {# nights}}, {roomsText} from (room only)',
                description: 'Duration with rooms and room-only suffix for offer tile pricing',
              },
              { nights, roomsText },
            )
          default:
            return intl.formatMessage(
              {
                id: 'jWQ3t1bWlJ_Vvv1o6Zhs',
                defaultMessage: '{nights, plural, one {# night} other {# nights}}, {roomsText} from',
                description: 'Duration with rooms for offer tile pricing on the offer page',
              },
              { nights, roomsText },
            )
        }
      }
      switch (suffix) {
        case 'roomOnly':
          return intl.formatMessage(
            {
              id: 'iQpkazYZDkikiTz7k4DU',
              defaultMessage: '{nights, plural, one {# night} other {# nights}} from (room only)',
              description: 'Duration with room-only suffix for offer tile pricing',
            },
            { nights },
          )
        default:
          return intl.formatMessage(
            {
              id: '7VdwC4oYAbZlUEurEkCc',
              defaultMessage: '{nights, plural, one {# night} other {# nights}} from',
              description: 'Duration in nights for offer tile pricing on the offer page',
            },
            { nights },
          )
      }
    }

    const formatDatesWithRooms = (dateString: string) => {
      if (showRooms) {
        switch (suffix) {
          case 'roomOnly':
            return intl.formatMessage(
              {
                id: 'M-TYl1wYk11V6F3DnHm6',
                defaultMessage: '{dateString}, {roomsText} from (room only)',
                description: 'Date string with rooms and room-only suffix for offer tile pricing',
              },
              { dateString, roomsText },
            )
          default:
            return intl.formatMessage(
              {
                id: 'T8vbzqHRlpjnkTYLfCTK',
                defaultMessage: '{dateString}, {roomsText} from',
                description: 'Date string with rooms for the offer tile pricing in the offer listing section',
              },
              { dateString, roomsText },
            )
        }
      }
      switch (suffix) {
        case 'roomOnly':
          return intl.formatMessage(
            {
              id: 'cHNEBEku52wgqIrjADa1',
              defaultMessage: '{dateString} from (room only)',
              description: 'Date string with room-only suffix for offer tile pricing',
            },
            { dateString },
          )
        default:
          return intl.formatMessage(
            {
              id: 'dLzZiszI3MdMG2x7wWqo',
              defaultMessage: '{dateString} from',
              description: 'Date string for offer tile pricing in the date selection or calendar picker',
            },
            { dateString },
          )
      }
    }

    const formatExtendedDatesWithNights = (dateString: string, nightCount: number) => {
      const nights = adjustNights(nightCount)
      if (showRooms) {
        switch (suffix) {
          case 'roomOnly':
            return intl.formatMessage(
              {
                id: 'cMkJQsPN9Ww6vpgfz1AQ',
                defaultMessage: '{dateString} ({nights, plural, one {# night} other {# nights}}), {roomsText} from (room only)',
                description: 'Extended dates with nights, rooms, and room-only suffix for offer tile pricing',
              },
              { dateString, nights, roomsText },
            )
          default:
            return intl.formatMessage(
              {
                id: 'Xvp77VROgiuzcx6qy8NY',
                defaultMessage: '{dateString} ({nights, plural, one {# night} other {# nights}}), {roomsText} from',
                description: 'Extended dates with nights and rooms for offer tile pricing',
              },
              { dateString, nights, roomsText },
            )
        }
      }
      switch (suffix) {
        case 'roomOnly':
          return intl.formatMessage(
            {
              id: 'lvkPPOI-k5o9kP7RYtow',
              defaultMessage: '{dateString} ({nights, plural, one {# night} other {# nights}}) from (room only)',
              description: 'Extended dates with nights and room-only suffix for offer tile pricing',
            },
            { dateString, nights },
          )
        default:
          return intl.formatMessage(
            {
              id: 'sqjnb97YceFz46c0IwxD',
              defaultMessage: '{dateString} ({nights, plural, one {# night} other {# nights}}) from',
              description: 'Extended dates with nights for the offer tile pricing',
            },
            { dateString, nights },
          )
      }
    }

    const formatLabelWithRooms = (label: string) => {
      if (showRooms) {
        switch (suffix) {
          case 'roomOnly':
            return intl.formatMessage(
              {
                id: 'MW5NoA5f1WrnfXRiWHs7',
                defaultMessage: '{label}, {roomsText} from (room only)',
                description: 'Duration label with rooms and room-only suffix for offer tile pricing',
              },
              { label, roomsText },
            )
          default:
            return intl.formatMessage(
              {
                id: 'OmesOM5I9lwd_tehyPX4',
                defaultMessage: '{label}, {roomsText} from',
                description: 'Duration label with rooms for the offer tile pricing',
              },
              { label, roomsText },
            )
        }
      }
      switch (suffix) {
        case 'roomOnly':
          return intl.formatMessage(
            {
              id: 'SF-9Gf2LBLn9GzrjlzXD',
              defaultMessage: '{label} from (room only)',
              description: 'Duration label with room-only suffix for offer tile pricing',
            },
            { label },
          )
        default:
          return intl.formatMessage(
            {
              id: 'w7IObO9tHH5zZJ8XWzI5',
              defaultMessage: '{label} from',
              description: 'Duration label for offer tile pricing on the offer listing or detail page',
            },
            { label },
          )
      }
    }

    if (directSearchPrices && offerMetadata?.extendedAvailability) {
      const dateString = formatDateRangesForAlternativeDates(offerMetadata.extendedAvailability.checkIn, offerMetadata.extendedAvailability.checkOut)
      return formatExtendedDatesWithNights(dateString, pkg.duration)
    } else if (directSearchPrices?.duration) {
      return formatNightsWithRooms(directSearchPrices.duration)
    } else if (hasDates) {
      return formatNightsWithRooms(pkg.duration)
    } else if (!isAnytimeSearch && hasSuggestedCheckInCheckOut && suggestedDates) {
      const dateString = buildSuggestedDatesString(suggestedDates.checkIn, suggestedDates.checkOut)
      return formatDatesWithRooms(dateString)
    } else if (suggestedDates && duration) {
      return formatNightsWithRooms(duration)
    } else if (pkg.duration) {
      return formatNightsWithRooms(pkg.duration)
    } else if (offer.tileDurationLabel) {
      return formatLabelWithRooms(offer.tileDurationLabel.toLocaleLowerCase())
    } else {
      return intl.formatMessage({
        id: 'LuIHWTR4wpAYnzrjXHhb',
        defaultMessage: 'From',
        description: 'Fallback price prefix when no duration is available for offer tile pricing',
      })
    }
  }, [filters?.flexibleNights,
    filters?.checkIn,
    filters?.checkOut,
    filters?.rooms?.length,
    suggestedDates,
    overrideRoomsNumber,
    isMultiRoomEnable,
    offer.tileDurationLabel,
    offer.type,
    directSearchPrices,
    offerMetadata?.extendedAvailability,
    hasDates,
    hasSuggestedCheckInCheckOut,
    duration,
    pkg.duration,
    noGoogleAffiliations,
    adjustNights,
    intl,
    showCompact])

  const durationResolved = useMemo(() => {
    if (hasDates) return pkg.duration
    if (suggestedDates && duration) return duration
    if (pkg.duration) return pkg.duration
  }, [duration, hasDates, pkg.duration, suggestedDates])

  const saleUnit = useMemo(() => {
    if (isVillas) return 'total'
    if (isPricePerNight) return 'night'
    if (rooms.length) return 'total'
    if (offer.saleUnit.toLowerCase() === 'cruise') return 'person'
    return offer.saleUnit
  }, [isPricePerNight, isVillas, offer.saleUnit, rooms.length])

  const showModal = useContext(ModalContext)
  const openCruiseDiscountTermsAndConditionsModal = useCallback(() => {
    showModal(<CruisesDiscountTsAndCsModal />)
  }, [showModal])

  const defaultPricingToDisplay = useMemo(() => {
    return {
      ...defaultPricing,
      saleUnit,
    }
  }, [defaultPricing, saleUnit])

  const memberPricingToDisplay = useMemo(() => {
    if (!memberPricing) return undefined

    return {
      ...memberPricing,
      saleUnit,
    }
  }, [memberPricing, saleUnit])

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  const isSearchPage = pathname.includes('search')
  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  const marginLPC = pkg.roomRate?.marginPercentage
  const marginValueLPC = marginLPC ? hotelPrice * marginLPC / 100 : 0
  const displayMarginLPC = Boolean(isSpoofed && showMarginInSpoofMode && marginLPC && marginValueLPC)

  useEffect(() => {
    const durationToDispatch = shouldUseRealSearchPrices ? directSearchPrices?.duration : durationResolved
    const availabilityToDispatch = shouldUseRealSearchPrices ? offerMetadata?.available : defaultPricingToDisplay.price !== 0
    const roomRateId = shouldUseRealSearchPrices ? offerMetadata?.pricing?.lowestPriceRoomRateId : undefined

    if (!shouldUseRealSearchPrices || offerMetadata) {
      dispatchOfferListEvent({
        type: OfferListEvents.pricingCalculated,
        price: hidePrice ? undefined : defaultPricingToDisplay.price,
        roomRateId: hidePrice ? undefined : roomRateId,
        memberPrice: hidePrice ? undefined : memberPricingToDisplay?.price,
        duration: hidePrice ? undefined : durationToDispatch,
        extendedAvailability: offerMetadata?.extendedAvailability,
        availability: !!availabilityToDispatch,
      })
    }
  }, [defaultPricingToDisplay.price, directSearchPrices?.duration, dispatchOfferListEvent, durationResolved, hidePrice, memberPricingToDisplay?.price, offerMetadata, shouldUseRealSearchPrices])

  const priceDisplaySize: 'S' | 'L' = showCompact ? 'S' : 'L'
  const priceDisplaySaleUnit = (saleUnit === 'night' || saleUnit === 'person') ? saleUnit : 'total'
  const isMaldivesCharterBundle = 'campaignTags' in offer && offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
  const burnResult = useSearchBurnPoints(defaultPricingToDisplay.price, getLoyaltyProductType(offer) ?? 'hotel_flash', getHotelEncryptedMargin(offer.type, pkg), { disabled: isMaldivesCharterBundle })
  const compactDurationText = durationResolved ?
      intl.formatMessage(
        {
          id: 'tJvJ4kkFeIVcfHOyX8AG',
          defaultMessage: '{nights, plural, one {# night} other {# nights}}',
          description: 'Compact search tile pricing duration shown above the price',
        },
        { nights: adjustNights(durationResolved) },
      ) :
    undefined

  let priceDisplay: React.ReactNode
  if (burnResult.enabled) {
    priceDisplay = <LoyaltyBurnPriceDisplay
      totalPrice={defaultPricingToDisplay.price}
      pointsToBurn={burnResult.pointsToBurn}
      priceInPoints={burnResult.priceInPoints}
      memberPrice={memberPricingToDisplay?.price}
      size={priceDisplaySize}
      saleUnit={priceDisplaySaleUnit}
      hideZeroCash={burnResult.hideZeroCash}
    />
  } else {
    priceDisplay = <LuxPlusPriceStack
      price={defaultPricingToDisplay.price}
      saleUnit={priceDisplaySaleUnit}
      memberPrice={memberPricingToDisplay?.price}
      size={priceDisplaySize}
      testid="search-price"
      desktopHorizontalAlign={desktopAlign}
      offerConfig={offer.luxPlus}
    />
  }

  if (hidePrice) {
    return null
  }

  if (compactExperimentLayout) {
    return <CompactPricingContainer className={className}>
      {isReservableForZero && <ReserveForZeroTag />}
      {compactDurationText && <Caption variant="medium" colour="neutral-two">
        {compactDurationText}
      </Caption>}
      {priceDisplay}
      <CompactAgentHubCommission>
        <PriceRowAgentHubCommission
          productType={offer.productType}
          offerId={offer.id}
          offerLocations={offerLocations}
          vendorName={vendorName}
          packageId={pkg.id}
        />
      </CompactAgentHubCommission>
    </CompactPricingContainer>
  }

  return (
    <Group
      className={className}
      direction="vertical"
      horizontalAlign={align}
      desktopHorizontalAlign={desktopAlign}
      gap={4}
    >
      <Group direction="vertical" horizontalAlign={align} desktopHorizontalAlign={desktopAlign} noDisplayWhenEmpty>
        {isReservableForZero && <ReserveForZeroTag />}
      </Group>
      <Group direction="vertical" horizontalAlign={align} desktopHorizontalAlign={desktopAlign}>
        <PriceRowPriceCaption>{durationText}</PriceRowPriceCaption>
        {priceDisplay}
        {shouldShowValue && isCruise && !isPricePerNight && <PriceRowValueDiscountWithCaption
          data-testid="value-box"
          size="M"
          originalValue={Math.ceil(valueToDisplay)}
          discountPercentage={shouldShowRate ? discountPercent : undefined}
          onInfoIconClick={openCruiseDiscountTermsAndConditionsModal}
        />}
        {showStaysFromLabel && staysFrom && <PriceRowCaption>
          <FormattedMessage
            id="7PFPr3tSopW8Hgmmb_Dy"
            defaultMessage="Stays from {staysFrom, plural, one {# night} other {# nights}} available"
            description="Caption indicating minimum stay duration available for an always-on offer"
            values={{ staysFrom }}
          />
        </PriceRowCaption>}
        {!!pkg.roomOccupancy && offer.type === 'tour' && <PriceRowCaption>{pkg.roomOccupancy}</PriceRowCaption>}
        {showIncludedGuestsExceeded && (
          <AdditionalGuestsPopup
            complex={false}
            description={intl.formatMessage({
              id: 'P-XvcuA36BAX7D0_zxA7',
              defaultMessage: '+ Extra guest surcharge',
              description: 'Caption indicating there is an additional surcharge for extra guests',
            })}
            modalContent={pkg.roomPolicyDescription}
          />
        )}
        {shouldShowValue && !isCruise && <PriceRowValueDiscountWithCaption
          data-testid="value-box"
          size="M"
          originalValue={Math.ceil(valueToDisplay)}
          discountPercentage={shouldShowRate ? discountPercent : undefined}
        />}
        <Group direction="vertical" horizontalAlign={align} desktopHorizontalAlign={desktopAlign} gap={4}>
          {showPriceDetails && durationResolved && <OfferPriceDetailsRow
            trigger="price-row"
            triggerSize="M"
            offer={offer}
            duration={durationResolved}
            propertyFees={propertyFees}
            hotelPrice={hotelPrice}
            taxesAndFees={taxesAndFees}
            rooms={rooms}
            extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
            dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property?.taxesAndFeesContent : ''}
            hotelMemberPrice={hotelMemberPrice}
            showMemberPrice={showMemberPrice}
            context={isSearchPage ? 'search' : undefined}
            hideIncludesTaxesAndFees={hideIncludesTaxesAndFees}
            cta={
              <TextButton kind="primary" fit="flex" to={offerUrl ?? offerPageURL(offer)}>
                {intl.formatMessage({
                  id: 'DuXrIdvxR5wDe6iOykWu',
                  defaultMessage: 'View Offer',
                  description: 'CTA button in price details row to view the full offer',
                })}
              </TextButton>
            }
          />}
          {displayMarginLPC && (<PriceRowMarginText marginValue={marginValueLPC} margin={marginLPC! / 100} type="le_estimate" colour="critical" />)}
          <PriceRowAgentHubCommission
            productType={offer.productType}
            offerId={offer.id}
            offerLocations={offerLocations}
            vendorName={vendorName}
            packageId={pkg.id}
          />
        </Group>
      </Group>
    </Group>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState) => {
  return {
    isSpoofed: appState.auth.account.isSpoofed,
    pathname: appState.router.location.pathname,
  }
})(OfferTilePricing)
